/**
 * The message as one real person in this segment would receive it.
 *
 * ## Why this is not `WaPreview`, and why `WaPreview` must stay as it is
 *
 * `routes/templates/whatsapp/WaPreview.tsx` draws the same four parts in the
 * same order and **refuses to substitute anything**: it prints `{{1}}` as
 * `{{1}}` and says so underneath, because on the template editor there is no
 * campaign, no audience and nothing that records what a position stands for —
 * "a fabricated 'Priya' here would be Wend inventing a value it cannot supply
 * at send time". That reasoning is still exactly right there, so that file was
 * not widened to take a substitution map. Widening it would have replaced a
 * correct refusal with an option, and an option gets passed.
 *
 * Here a campaign exists, it has a segment, and `wa_bindings` records what
 * fills each position. So substitution is not an invention: it is the answer,
 * and the whole reason this screen is worth looking at before scheduling.
 *
 * ## Nothing on this page substitutes anything
 *
 * Every string below arrives **already resolved from the API**. The endpoint
 * runs `loadWhatsAppMessageSource` and `waValuesForRecipient` — the send's own
 * functions, against a real contact from the campaign's own segment — and hands
 * back finished text. This component draws it and does not touch it.
 *
 * That is deliberate and it is the defect this whole panel was written to
 * avoid. A substitution loop written in the browser would be confidently right
 * on screen and wrong on the wire, and the failure would surface as a thousand
 * people receiving a sentence with the wrong word in it, with no error anywhere
 * — the same shape as a rate computed over the wrong denominator. There is
 * exactly one implementation of "what goes in `{{1}}`" in this repository and
 * it is not in `apps/web`.
 *
 * ## `usedFallback` is a fact, not a warning — and it is toned like one
 *
 * A value that came from the fallback is chipped rather than coloured red.
 * Falling back is the ordinary, designed behaviour for a contact with nothing
 * in that column — what a person needs to see is *which* of the values on this
 * bubble is the one they typed rather than one their list supplied, because a
 * misspelt `extras.Branch` makes every value on screen look deliberate. The
 * count that makes that visible at scale is the cohort chip on the binding row;
 * this chip makes it visible for the one person in front of them.
 *
 * It was **amber** until slice 49's design review measured the rendered page:
 * this chip, the binding row's `ignored` chip and a cohort chip reading "1,284
 * of 1,284 fall back" were the same rgb(245,184,81) pill at the same size, so
 * the one signature of a misspelt column wore the same colour as a fact this
 * very header calls ordinary. Three facts in one tone is one tone carrying
 * nothing. Neutral here, and the colour is spent where it means something.
 *
 * ## An unfilled position is drawn, because the API cannot draw it
 *
 * `buildWaPreview` maps `values` from the bindings, so a position the template
 * needs and nothing fills produces **no row at all** — while `renderWaText`
 * leaves the literal `{{3}}` sitting in `body` in the same colour and weight as
 * every other word. That literal is exactly what would go to Meta, which
 * refuses a message containing it, and the panel drew the finished bubble with
 * no mark on it anywhere.
 *
 * So the panel passes `unfilledPositions` — it is the only side that knows,
 * because the row model holds the template's positions and the response does
 * not — and each one takes a row in the same list, in position order beside the
 * values, chipped `bad`. The list is the one place a reader compares position
 * against outcome, so a position with no outcome belongs in it rather than
 * beside it.
 *
 * ## SAMPLE_VALUES_NOTICE is deliberately absent, and `sample === null` is why
 * the chip above the bubble is not optional
 *
 * The template editor prints it because its values are invented. These are
 * usually not: they are one real contact's, which is the point. Printing
 * "sample values — not real contact data" over a real contact's name would be
 * a false statement about the provenance of everything below it. The contact
 * is named by a chip instead — `sample` carries an id and a name, and no
 * address and no phone number.
 *
 * **`sample === null` is the exception, and it used to render as nothing.** The
 * segment reached nobody on WhatsApp, so there was no contact to resolve
 * against, every position took its fallback, and the panel drew a complete,
 * confident message under "What one recipient sees" — for a message no
 * recipient exists to receive. A missing chip cannot carry that: "no contact at
 * all" and "a real contact whose name column is empty" rendered identically,
 * and the second is ordinary. So the null draws its own chip, in the place the
 * name would have been, and only the name is left unsaid when there is a
 * contact without one. Nothing measured and nobody measured are different
 * facts, exactly as zero and "not measured" are everywhere else here.
 *
 * ## A value below CAN be a real address or phone number, by design
 *
 * The sentence here used to say the contract carried neither into this
 * component, and slice 49's review was right that it was false: a binding whose
 * source is `email` or `phoneE164` resolves to that contact's own field, comes
 * back as `values[].value`, is listed under the bubble and is substituted into
 * `body`. That is what "what one recipient sees" means — a preview that blanked
 * the one value the message is built around would be a picture of a different
 * message.
 *
 * It is the workspace's own uploaded contact, shown to the workspace, on a
 * screen it opened. This component draws it and nothing else: it is not logged,
 * not put in a URL, and not sent anywhere.
 */
import type { CampaignWaPreview, WaPreviewValue } from '@wend/shared';
import { Badge, cn } from '@wend/ui';

import { WA_HEADER_FORMAT_LABEL } from '../../lib/whatsapp-display';
import { waPositionToken } from './wa-binding-rows';

export interface WaBoundPreviewProps {
  preview: CampaignWaPreview;
  /**
   * Positions the template needs that nothing fills, so their `{{n}}` reaches
   * Meta literally. Empty by default: every other caller of this component has
   * a finished binding list, and a required prop would make them all say so.
   */
  unfilledPositions?: readonly number[];
}

export function WaBoundPreview({ preview, unfilledPositions = [] }: WaBoundPreviewProps) {
  /*
   * One list, position order, values and gaps together. Two lists would put
   * the gap after the last value, which on a four-position template reads as a
   * footnote about a different message — and the comparison this list exists
   * for is position against outcome, down one column.
   */
  const rows: ReadonlyArray<{ position: number; value: WaPreviewValue | null }> = [
    ...preview.values.map((value) => ({ position: value.position, value })),
    ...unfilledPositions.map((position) => ({ position, value: null })),
  ].sort((a, b) => a.position - b.position);

  return (
    <div className="flex flex-col gap-3">
      {preview.sample === null ? (
        <div>
          <Badge tone="neutral">no contact to resolve against</Badge>
        </div>
      ) : (
        preview.sample.name !== null && (
          <div>
            <Badge tone="neutral">{preview.sample.name}</Badge>
          </div>
        )
      )}

      <div className="rounded-base bg-ink/60 p-4">
        <div className="max-w-[22rem] rounded-lg rounded-tl-sm border border-line bg-white/[0.05] p-3 shadow-sm">
          {preview.headerMedia !== null && (
            <div className="mb-2 grid h-24 place-items-center rounded-base border border-dashed border-line-strong text-[0.75rem] uppercase tracking-[0.1em] text-muted">
              {WA_HEADER_FORMAT_LABEL[preview.headerMedia]} header
            </div>
          )}

          {preview.header !== null && (
            <p className="mb-1.5 text-[0.95rem] font-semibold leading-snug text-text">
              {preview.header}
            </p>
          )}

          <p className="whitespace-pre-wrap break-words text-[0.92rem] leading-relaxed text-text">
            {preview.body}
          </p>

          {preview.footer !== null && (
            <p className="mt-2 text-[0.78rem] leading-snug text-muted">{preview.footer}</p>
          )}
        </div>

        {preview.buttons.length > 0 && (
          <div className="mt-2 flex max-w-[22rem] flex-col gap-1.5">
            {preview.buttons.map((button) => (
              <div
                key={button.index}
                className={cn(
                  'rounded-lg border border-line bg-white/[0.05] px-3 py-2 text-center',
                  'text-[0.88rem] text-info',
                )}
              >
                {button.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <ol className="flex flex-col gap-1.5">
          {rows.map((row) => (
            <li key={row.position} className="flex flex-wrap items-center gap-2">
              <Badge tone="neutral" className="font-mono">
                {waPositionToken(row.position)}
              </Badge>

              {row.value === null ? (
                <Badge tone="bad">unfilled</Badge>
              ) : (
                <>
                  {/* The field that produced the value, beside the value it
                      produced. `values[].source` has been on the contract since
                      slice 49 and was drawn nowhere, so reading "your branch"
                      here meant carrying the row above in your head to find out
                      which column it came from — and that mapping is precisely
                      what a misspelling breaks. It is the wire string, because
                      the wire string is what is misspelt. */}
                  <span className="font-mono text-[0.75rem] text-muted">{row.value.source}</span>
                  <span aria-hidden="true" className="text-[0.75rem] text-muted">
                    →
                  </span>
                  <span className="min-w-0 break-words text-[0.86rem] text-text">
                    {row.value.value}
                  </span>
                  {row.value.usedFallback && <Badge tone="neutral">fallback</Badge>}
                </>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
